// Visitor regions: what varies by market, one strategy per region (#4967,
// ADR 0059). The browser's twin of cloud_functions/profile_manager_v2/
// regions.py, which is the authority; test_regions.py pins the codes, the
// time zones, the sections and the prices equal. Its one import is the
// pricing home (constants/seasonPricing.js), itself import-free.
//
// A caller asks the region it was handed (`region.showsSection(id)`) and
// never branches on a country code. A new market is a new entry in REGIONS
// built by makeRegion; no call site changes.
//
// #4966: the currency, the card country a local price is for, the Intl
// locale, and the Season Pass price live on the region too, the price read
// from the one pricing home (constants/seasonPricing.js, this module's only
// import). The page displays it; payment_manager_v2 charges it, and only to
// a card issued in the region's country (ADR 0060).
//
// #4985 (founder 2026-10-21: master's for everybody, only the currency and
// the price change): every region offers the master's waitlist, carries the
// planned master's price /masters shows (`mastersPrice`, never charged) and
// says whether its visitors apply from abroad (`international`: the page
// adds the international points). India keeps the home-page section; the
// default region's home page gets the one link line (MASTERS_LINK).

import {
    SEASON_PASS_INR, THROUGH_GRADUATION_INR, THROUGH_GRADUATION_USD, seasonPassPriceUsd, formatPrice,
} from '../constants/seasonPricing';

// The landing page's Master's abroad section and its waitlist.
export const MASTERS_ABROAD = 'masters_abroad';
// The default region's home-page line to /masters (#4985), placed low.
export const MASTERS_LINK = 'masters_link';

// The planned master's price, in the major unit (regions.py
// MASTERS_PRICE_USD_CENTS / MASTERS_PRICE_INR_PAISE are these x 100, pinned
// by test_regions.py). Shown as the price when program pages open.
export const MASTERS_PRICE_USD = 149;
export const MASTERS_PRICE_INR = 9999;

const makeRegion = ({ code, timeZones, landingSections, waitlists, currency, cardCountry, locale,
    seasonPassAmount, graduationAmount, mastersAmount, international }) => Object.freeze({
    code,
    timeZones: Object.freeze([...timeZones]),
    landingSections: Object.freeze([...landingSections]),
    waitlists: Object.freeze([...waitlists]),
    currency,
    cardCountry,
    locale,
    international,
    showsSection(id) { return this.landingSections.includes(id); },
    offers(waitlist) { return this.waitlists.includes(waitlist); },
    matchesTimeZone(tz) { return typeof tz === 'string' && this.timeZones.includes(tz); },
    // A price of its own (shown with a switch back to US dollars).
    pricesLocally() { return this.cardCountry !== null; },
    // {currency, amount, label, renews}: the Season Pass as this market buys
    // it. A local price is one payment for the year (it never renews: the
    // server holds it for the card check, which Checkout cannot do for a
    // subscription); the US Pass renews each year unless cancelled.
    seasonPass(now = new Date()) {
        const amount = seasonPassAmount(now);
        return Object.freeze({ currency: this.currency, amount,
            label: formatPrice(amount, this.currency, this.locale), renews: !this.pricesLocally() });
    },
    // {currency, amount, label}: Through Graduation as this market is shown
    // it (#5128, the landing page's cost band). payment_manager_v2 charges
    // its own figure (products.py / regions.py, pinned by test_pass_terms.py).
    throughGraduation() {
        return Object.freeze({ currency: this.currency, amount: graduationAmount,
            label: formatPrice(graduationAmount, this.currency, this.locale) });
    },
    // {currency, amount, label}: the planned master's price here (#4985).
    mastersPrice() {
        return Object.freeze({ currency: this.currency, amount: mastersAmount,
            label: formatPrice(mastersAmount, this.currency, this.locale) });
    },
});

// India: Asia/Calcutta is the old alias some browsers still report. Sri
// Lanka shares the offset but has its own zone, so it is not matched.
const INDIA = makeRegion({
    code: 'in',
    timeZones: ['Asia/Kolkata', 'Asia/Calcutta'],
    landingSections: [MASTERS_ABROAD],
    waitlists: [MASTERS_ABROAD],
    currency: 'inr',
    cardCountry: 'IN',
    locale: 'en-IN',
    seasonPassAmount: () => SEASON_PASS_INR,
    graduationAmount: THROUGH_GRADUATION_INR,
    mastersAmount: MASTERS_PRICE_INR,
    international: true,
});

// Everyone the signal does not place elsewhere, the US included.
const DEFAULT_REGION = makeRegion({
    code: 'us',
    timeZones: [],
    landingSections: [MASTERS_LINK],
    waitlists: [MASTERS_ABROAD],
    currency: 'usd',
    cardCountry: null,
    locale: 'en-US',
    seasonPassAmount: (now) => seasonPassPriceUsd(now),
    graduationAmount: THROUGH_GRADUATION_USD,
    mastersAmount: MASTERS_PRICE_USD,
    international: false,
});

// The registry, in lookup order (first time-zone match wins).
const KINDS = [INDIA, DEFAULT_REGION];
export const REGIONS = Object.freeze(Object.fromEntries(KINDS.map((r) => [r.code, r])));
export const DEFAULT = DEFAULT_REGION;

/** The region for a code (the ?region= override), else null for an unknown code. */
export const regionForCode = (code) => (
    typeof code === 'string' && Object.prototype.hasOwnProperty.call(REGIONS, code.trim().toLowerCase())
        ? REGIONS[code.trim().toLowerCase()]
        : null
);

/** The region whose time zones hold `tz`, else the default. */
export const regionForTimeZone = (tz) => KINDS.find((r) => r.matchesTimeZone(tz)) || DEFAULT;
